import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { FooterComponent } from '../../components/data-display/footer/footer.component';
import { HeaderComponent } from '../../components/data-display/header/header.component';
import { FontStore } from '../../services/api/font/font-store.service';
import { LanguagePipe } from '../../pipes/language/language.pipe';
import { ConfigurationStore } from '../../services/api/configuration/configuration-store.service';
import {
  ESiteContext,
  IConfiguration,
  ICorporateDesign,
  IFont,
} from '@web/dtos';
import { CorporateDesignService } from '../../services/corporate-design/corporate-design.service';
import { AppConfigService } from '../../services/app-config.service';
import { SnackbarComponent } from '../../components/data-display/snackbar/snackbar.component';
import { AuthProviderFactory } from '../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../services/auth/auth.provider';
import { SiteContextService } from '../../services/site-context/site-context.service';
import { OnboardingStore } from '../../services/api/user/onboarding/onboarding.store';
import { OnboardingDialogService } from '../../services/user-onboarding/onboarding-dialog.service';
import { ThemeComponent } from '../../theming';

@Component({
  selector: 'aw-shop',
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SnackbarComponent,
    ThemeComponent
  ],
  templateUrl: './shop-page.component.html',
  styles: ``,
})
export class ShopPageComponent implements OnInit {
  private readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  private readonly configService: AppConfigService = inject(AppConfigService);
  private readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  private readonly languagePipe: LanguagePipe = inject(LanguagePipe);
  private readonly fontStore: FontStore = inject(FontStore);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly onboardingDialogService: OnboardingDialogService = inject(
    OnboardingDialogService
  );
  private readonly onboardingStore: OnboardingStore = inject(OnboardingStore);
  private readonly corporateDesign: Signal<ICorporateDesign> = computed(() =>
    this.corporateDesignService.corporateDesign()
  );
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);

  private readonly apiUrl: string = this.configService.get().general.cmsUrl;

  public readonly configuration: Signal<IConfiguration[]> =
    this.configurationStore.configuration;

  constructor() {
    this.siteContextService.setContext(ESiteContext.Shop);
    effect((): void => {
      if (this.fontStore.fonts()) {
        this.fontStore
          .fonts()
          .filter((font: IFont): boolean => font.name !== '')
          .forEach((font: IFont): void => this.loadCustomFont(font));
      }
    });
    effect((): void => {
      if (this.corporateDesign() && this.corporateDesign().font?.name !== '') {
        this.loadStandardFont(this.corporateDesign());
      }
    });
    effect((): void => {
      if (
        this.corporateDesign() &&
        this.corporateDesign().faviconFilename &&
        this.corporateDesign().faviconFilename !== ''
      )
        this.loadFavicon();
    });
    effect((): void => {
      if (
        this.corporateDesign() &&
        this.corporateDesign().tabs &&
        this.corporateDesign().tabs.length > 0
      )
        this.loadTab(this.corporateDesign());
    });
  }

  async ngOnInit(): Promise<void> {
    await this.auth.initialize();

    if (!this.auth.isLoggedIn()) {
      return;
    }

    await this.onboardingStore.load();

    if (!this.onboardingStore.isCompleted()) {
      this.onboardingDialogService.open();
    }
  }

  loadCustomFont(font: IFont): void {
    const styleId = `shop-font-${font.id ?? encodeURIComponent(font.name)}`;
    if (document.getElementById(styleId)) {
      return;
    }

    const fontUrl = `${this.apiUrl}/fonts/${encodeURIComponent(font.name)}`;
    const fontFormat: string = font.name.toLowerCase().endsWith('.woff2')
      ? 'woff2'
      : 'truetype';
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
    @font-face {
      font-family: '${this.escapeCssString(font.name)}';
      src: url('${fontUrl}') format('${fontFormat}');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;
    document.head.appendChild(style);
  }

  loadStandardFont(corporateDesign: ICorporateDesign): void {
    document.getElementById('shop-active-font')?.remove();

    const fontFamily: string = corporateDesign.font?.name
      ? `'${this.escapeCssString(corporateDesign.font.name)}', sans-serif`
      : `'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
    const style = document.createElement('style');
    style.id = 'shop-active-font';
    style.innerHTML = `
      aw-shop,
      aw-shop *:not(.material-symbols-rounded):not(.material-icons) {
        font-family: ${fontFamily} !important;
      }

      aw-shop {
        font-size: ${corporateDesign.fontSizeText}px;
      }

      aw-shop .material-symbols-rounded {
        font-family: 'Material Symbols Rounded' !important;
      }
    `;
    document.head.appendChild(style);
  }

  private escapeCssString(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  }

  loadFavicon(): void {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = this.apiUrl + '/corporate-design/favicon';
    link.type = 'image/x-icon';
    document.head.appendChild(link);
  }

  loadTab(corporateDesign: ICorporateDesign): void {
    const title = document.head.querySelector('title');
    if (title) {
      title.innerHTML = this.languagePipe.transform(
        corporateDesign.tabs[0] ?? 'Webshop'
      );
    }
  }
}
